import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

const STAY_CART_RELEASE = '260927-v3';

/* ------------------------------------------------------------------------
   Cart writes in flight. Checkout waits until every add, change, update and
   clear has settled, whichever script started it, so the shopper never pays
   for a cart that is still changing.
   ------------------------------------------------------------------------ */
let cartBusy = 0;

const setCartBusy = (delta) => {
  cartBusy = Math.max(0, cartBusy + delta);
  document.documentElement.toggleAttribute('data-stay-cart-busy', cartBusy > 0);
  document.dispatchEvent(new CustomEvent('stay:cart-busy'));
};

const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
const nativeFetch = window.fetch.bind(window);

window.fetch = (input, init) => {
  const url = String(input instanceof Request ? input.url : input);
  if (!CART_WRITE.test(new URL(url, window.location.href).pathname)) return nativeFetch(input, init);
  setCartBusy(1);
  return nativeFetch(input, init).finally(() => setCartBusy(-1));
};

document.addEventListener('stay:rakhi-add', async (event) => {
  const source = event.target;
  const items = event.detail?.items;
  if (!(source instanceof HTMLElement) || !Array.isArray(items) || !items.length) return;

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({
        items,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Unable to add these items.');
    }

    source.dispatchEvent(
      new CartAddEvent({}, 'rakhi-bogo-30', {
        source: 'rakhi-offers',
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        sections: result.sections,
      })
    );
    source.dispatchEvent(new CustomEvent('stay:rakhi-add:success'));
  } catch (error) {
    source.dispatchEvent(new CustomEvent('stay:rakhi-add:error', {
      detail: {message: error instanceof Error ? error.message : 'Unable to add these items.'},
    }));
  }
});

const isGoKwikReady = () =>
  typeof window.onCheckoutNow === 'function' &&
  typeof window.onCheckoutClick === 'function' &&
  typeof window.gokwikSdk?.initCheckout === 'function';

class StayCartUpsells extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick = async (event) => {
    const button = event.target.closest('[data-stay-cart-upsell]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    const offerType = button.dataset.stayCartOffer;
    const properties = {
      _stay_cart_release: STAY_CART_RELEASE,
    };
    if (offerType) properties._stay_cart_offer = offerType;

    const status = this.querySelector('[data-stay-cart-upsell-status]');
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);

    button.disabled = true;
    button.classList.add('is-adding');
    button.setAttribute('aria-busy', 'true');
    if (status) status.textContent = '';
    let added = false;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to add this item.');
      }

      this.dispatchEvent(
        new CartAddEvent({}, `stay-cart-upsell-${variantId}`, {
          source: 'stay-cart-upsells',
          itemCount: 1,
          variantId: String(variantId),
          sections: result.sections,
        })
      );
      added = true;
    } catch (error) {
      if (status) status.textContent = error instanceof Error ? error.message : 'Unable to add this item.';
    } finally {
      const resetButton = () => {
        button.disabled = false;
        button.classList.remove('is-adding');
        button.removeAttribute('aria-busy');
      };
      if (added) {
        window.setTimeout(resetButton, 600);
      } else {
        resetButton();
      }
    }
  };
}

if (!customElements.get('stay-cart-upsells')) {
  customElements.define('stay-cart-upsells', StayCartUpsells);
}

const repairCartStructure = (event) => {
  const current = document.querySelector('cart-items-component');
  if (!(current instanceof HTMLElement)) return;

  const sectionId = current.dataset.sectionId;
  const sectionHtml = sectionId ? event.detail?.data?.sections?.[sectionId] : null;
  if (!sectionHtml) return;

  const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const incoming = parsed.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
  if (!(incoming instanceof HTMLElement)) return;

  const incomingHasItems = Boolean(incoming.querySelector('[ref="cartItemCount"]'));
  const syncStructure = () => {
    const rendered = document.querySelector('cart-items-component');
    if (!(rendered instanceof HTMLElement)) return;

    const renderedHasItems = Boolean(rendered.querySelector('[ref="cartItemCount"]'));
    const renderedIsEmpty = Boolean(rendered.querySelector('.stay-cart-empty'));
    if (renderedHasItems === incomingHasItems && renderedIsEmpty === !incomingHasItems) return;

    const incomingChildren = Array.from(incoming.childNodes, (node) => document.importNode(node, true));
    rendered.replaceChildren(...incomingChildren);
    rendered.className = incoming.className;
    document
      .querySelector('.cart-drawer__dialog')
      ?.classList.toggle('cart-drawer--empty', !incomingHasItems);
  };

  syncStructure();
  window.requestAnimationFrame(syncStructure);
};

document.addEventListener('cart:update', repairCartStructure);

const markProductForm = (form) => {
  if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) return;
  if (form.querySelector('input[name="properties[_stay_cart_release]"]')) return;

  const marker = document.createElement('input');
  marker.type = 'hidden';
  marker.name = 'properties[_stay_cart_release]';
  marker.value = STAY_CART_RELEASE;
  form.append(marker);
};

const markProductForms = () => {
  document.querySelectorAll('form[action*="/cart/add"]').forEach(markProductForm);
};

document.addEventListener(
  'submit',
  (event) => {
    markProductForm(event.target);
  },
  true
);

// Whether this page view wants the drawer to open on add. Recorded because the
// drawer component consumes the `auto-open` attribute when it opens, so the
// attribute alone cannot be read back to answer the question later.
let autoOpenWanted = false;

const configureCartDrawer = () => {
  markProductForms();

  const drawer = document.querySelector('cart-drawer-component');
  if (!(drawer instanceof HTMLElement)) return;

  if (document.querySelector('#blp-bar')) {
    drawer.removeAttribute('auto-open');
    autoOpenWanted = false;
    return;
  }

  if (document.body.classList.contains('ct-template-product')) {
    drawer.setAttribute('auto-open', '');
    autoOpenWanted = true;
    return;
  }

  if (document.querySelector('[data-signature-shop], [data-rakhi-page]')) drawer.setAttribute('auto-open', '');
  autoOpenWanted = drawer.hasAttribute('auto-open');
};

configureCartDrawer();
document.addEventListener('shopify:section:load', configureCartDrawer);

// The drawer component removes `auto-open` when it opens and never restores it,
// so only the first add of a page view opened the cart. Re-arm after the open
// has been handled, leaving the current cycle alone.
document.addEventListener(
  'cart:update',
  (event) => {
    if (!autoOpenWanted || event.detail?.data?.didError) return;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const drawer = document.querySelector('cart-drawer-component');
        if (!drawer) return;
        // An empty cart has nothing to open for. Holding the attribute while
        // the cart is empty made the drawer open and close again immediately.
        const empty =
          event.detail?.data?.itemCount === 0 ||
          !!drawer.querySelector('.cart-drawer__dialog.cart-drawer--empty');
        if (empty) drawer.removeAttribute('auto-open');
        else drawer.setAttribute('auto-open', '');
      });
    });
  },
  true
);
document.addEventListener(
  'cart:update',
  (event) => {
    if (!document.querySelector('[data-signature-shop], [data-rakhi-page]') || event.detail?.data?.didError) return;
    const openDrawer = () => {
      const drawer = document.querySelector('cart-drawer-component');
      drawer?.setAttribute('auto-open', '');
      drawer?.open?.();
    };
    openDrawer();
    window.requestAnimationFrame(() => {
      openDrawer();
    });
  },
  true
);

// showModal() focuses the first button, the close X, which then wears a focus
// ring on every open. Start focus on the dialog instead; Tab still reaches X.
document.addEventListener(
  'dialog:open',
  (event) => {
    const dialog = event.target instanceof Element && event.target.querySelector('.cart-drawer__dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (document.activeElement?.matches('.cart-drawer__close-button')) dialog.focus({ preventScroll: true });
  },
  true
);

class StayCartCheckout extends HTMLElement {
  connectedCallback() {
    window.addEventListener('gokwikLoaded', this.enable);
    document.addEventListener('cart:update', this.enable);
    document.addEventListener('stay:cart-busy', this.enable);
    this.observer = new MutationObserver(this.enable);
    this.observer.observe(this, { childList: true, subtree: true });
    this.enable();
  }

  disconnectedCallback() {
    window.removeEventListener('gokwikLoaded', this.enable);
    document.removeEventListener('cart:update', this.enable);
    document.removeEventListener('stay:cart-busy', this.enable);
    this.observer?.disconnect();
  }

  enable = () => {
    const button = this.querySelector('[data-stay-gokwik-checkout]');
    if (!(button instanceof HTMLButtonElement)) return;

    button.disabled = !isGoKwikReady() || cartBusy > 0;
    button.classList.toggle('disabled', button.disabled);
  };

  handleCheckout = async (event) => {
    const button = event.target.closest('[data-stay-gokwik-checkout]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const drawer = this.closest('cart-drawer-component');
    const dialog = drawer?.querySelector('.cart-drawer__dialog');
    if (!drawer || !(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    button.disabled = true;
    this.dataset.handoff = 'closing';

    try {
      drawer.close();
      // A removal can re-enable checkout before the drawer's own sample check
      // has run. Apply the sample rule once more so GoKwik reads a cart that
      // holds the sample only if it still qualifies (order S4488).
      this.dataset.handoff = 'checking-cart';
      await syncGift();
      this.dataset.handoff = 'opening-checkout';

      if (!isGoKwikReady()) {
        throw new Error('Checkout is still loading. Please try again.');
      }

      button.disabled = false;
      button.classList.remove('disabled');
      window.onCheckoutNow(button);
    } catch (error) {
      button.disabled = false;
      const status = drawer.querySelector('[data-stay-cart-checkout-status]');
      if (status) {
        status.textContent = error instanceof Error ? error.message : 'Unable to open checkout.';
      }
      this.dataset.handoff = 'error';
    }
  };
}

if (!customElements.get('stay-cart-checkout')) {
  customElements.define('stay-cart-checkout', StayCartCheckout);
}

const routeCheckoutActivation = (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const controller = target.closest('stay-cart-checkout');
  if (!(controller instanceof StayCartCheckout)) return;

  if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') return;
  controller.handleCheckout(event);
};

window.addEventListener('pointerdown', routeCheckoutActivation, true);
window.addEventListener('keydown', routeCheckoutActivation, true);


/* ------------------------------------------------------------------------
   Free 2 mL sample.

   One rule: when the cart qualifies it holds exactly one sample, and the
   shopper picks the scent. One function owns that rule, one request enforces
   it, and the control is a plain <select> so the browser handles opening,
   positioning and keyboard use.
   ------------------------------------------------------------------------ */
const GIFT = {
  productId: 8862776656009,
  threshold: 149900,
  // Sample variant id to the product title of the scent it samples.
  scents: {
    48420947525769: 'STAY LOYAL',
    48420947558537: 'STAY BY THE OCEAN',
    48420947591305: 'STAY THE NIGHT',
    48420947624073: 'STAY IRRESISTIBLE',
    48420947656841: 'STAY SEDUCTIVE',
  },
};
GIFT.variants = Object.keys(GIFT.scents).map(Number);

let giftBusy = false;
let giftAgain = false;
// Settles when the current pass, and any pass queued behind it, is done.
let giftRun = Promise.resolve();

const giftCartUrl = `${Theme.routes.cart_url}.js`;
const readCart = async () => (await fetch(giftCartUrl, { headers: { Accept: 'application/json' } })).json();

const giftLines = (cart) => cart.items.filter((item) => item.product_id === GIFT.productId);
const giftQualifyingTotal = (cart) =>
  cart.items.filter((item) => item.product_id !== GIFT.productId).reduce((sum, item) => sum + item.line_price, 0);



/**
 * Sample scents the cart does not already hold. The sample exists to bring the
 * shopper back for a scent they have not bought, so a scent in the cart is
 * never offered. Worked out from the cart alone: when the sample is first
 * added, its picker is not on the page yet.
 */
const allowedGiftVariants = (cart) => {
  const owned = new Set(
    cart.items
      .filter((item) => item.product_id !== GIFT.productId)
      .map((item) => (item.product_title || '').trim().toUpperCase())
  );
  return GIFT.variants.filter((id) => !owned.has(GIFT.scents[id]));
};

/**
 * Makes the cart match the rule. `wanted` is the scent the shopper just chose,
 * if any. Every gift variant is set to zero except the one that should be
 * there, which is set to one, so duplicates cannot survive a pass.
 */
const syncGift = (wanted) => {
  if (giftBusy) {
    giftAgain = true;
    return giftRun;
  }
  giftRun = runGiftSync(wanted);
  return giftRun;
};

const runGiftSync = async (wanted) => {
  giftBusy = true;
  setCartBusy(1);
  try {
    const cart = await readCart();
    const lines = giftLines(cart);
    const allowed = allowedGiftVariants(cart);
    const qualifies = giftQualifyingTotal(cart) >= GIFT.threshold && allowed.length > 0;

    if (!qualifies && !lines.length) return;

    const target = qualifies
      ? [wanted, lines[0]?.variant_id].find((id) => allowed.includes(id)) ||
        allowed[Math.floor(Math.random() * allowed.length)]
      : null;
    const correct = lines.length === 1 && lines[0].quantity === 1 && lines[0].variant_id === target;
    if (correct) return;

    const updates = {};
    GIFT.variants.forEach((id) => {
      updates[id] = 0;
    });
    if (target) updates[target] = 1;

    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((node) => node.dataset.sectionId)
      .filter(Boolean);

    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: sections.join(','), sections_url: window.location.pathname }),
    });
    const updated = await response.json();
    if (!response.ok) return;

    sections.forEach((id) => {
      if (updated.sections?.[id]) morphSection(id, updated.sections[id]);
    });
    document.querySelector('cart-items-component')?.dispatchEvent(
      new CartUpdateEvent({}, sections[0], { source: 'stay-gift', sections: updated.sections, itemCount: updated.item_count })
    );
  } catch (error) {
    /* the cart is left as it stands; the sample is a bonus, not the order */
  } finally {
    giftBusy = false;
    // Start the queued pass before releasing this one, so checkout never
    // sees an idle cart between the two.
    const again = giftAgain;
    giftAgain = false;
    const next = again ? syncGift() : null;
    setCartBusy(-1);
    await next;
  }
};

document.addEventListener('change', (event) => {
  const select = event.target;
  if (select instanceof HTMLSelectElement && select.matches('[data-stay-gift-scent]')) {
    select.disabled = true;
    syncGift(Number(select.value));
  }
});

document.addEventListener('cart:update', (event) => {
  if (event.detail?.data?.source === 'stay-gift') return;
  syncGift();
});

syncGift();
